import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiService, type FeatureFlags as ApiFeatureFlags } from '../services/apiService'

export const useFeaturesStore = defineStore('features', () => {
  // Feature toggles
  const aiAssistantEnabled = ref(false)
  const musicEnabled = ref(false)
  const soundsEnabled = ref(false)  // Global sound control
  const animationsEnabled = ref(true)
  const funModeEnabled = ref(true)
  const reduceMotionEnabled = ref(false)
  const educationalChallengeEnabled = ref(false)
  const showtimeCreditsEnabled = ref(false)
  const mergeTheoryEnabled = ref(false)
  const flyingRobotEnabled = ref(false)
  const educationalResourcesEnabled = ref(false)
  const mobileAppDemoEnabled = ref(false)
  const drugInteractionsEnabled = ref(true)
  const billingSubscriptionEnabled = ref(true)
  const whatsNewEnabled = ref(false)
const comingSoonEnabled = ref(false)
const visualPreferencesEnabled = ref(false)
const conceptualPricingEnabled = ref(false)
  const accessibilitySettingsEnabled = ref(false)
  const dataExportEnabled = ref(false)
  const emailVerificationEnabled = ref(false)  // Default to disabled
  const tryDemoEnabled = ref(false)
  const autoScrollEnabled = ref(false)  // Default to disabled
  const lightModeDisabled = ref(false)  // Default to enabled (users can switch to light mode)
  const prioritizeHardcodedValues = ref(true)  // When true, hardcoded/localStorage values take priority over server values
  const pricingEnabled = ref(true)  // Default to enabled
  const termsAndConditionsDisabled = ref(true)  // Default to disabled (T&C link hidden). Hardcoded-only flag.
  const registrationDisabled = ref(true)  // Default to disabled (registration not allowed). Hardcoded-only flag.
  const googleSignupDisabled = ref(true)  // Default to disabled (Google signup not allowed). Hardcoded-only flag.

  // Computed properties
  const isAIAssistantEnabled = computed(() => aiAssistantEnabled.value)
  const isMusicEnabled = computed(() => musicEnabled.value)
  const areSoundsEnabled = computed(() => soundsEnabled.value)
  const areAnimationsEnabled = computed(() => animationsEnabled.value)
  const isFunModeEnabled = computed(() => funModeEnabled.value)
  const isReduceMotionEnabled = computed(() => reduceMotionEnabled.value)
  const isEducationalChallengeEnabled = computed(() => educationalChallengeEnabled.value)
  const isShowtimeCreditsEnabled = computed(() => showtimeCreditsEnabled.value)
  const isMergeTheoryEnabled = computed(() => mergeTheoryEnabled.value)
  const isFlyingRobotEnabled = computed(() => flyingRobotEnabled.value)
  const isEducationalResourcesEnabled = computed(() => educationalResourcesEnabled.value)
  const isMobileAppDemoEnabled = computed(() => mobileAppDemoEnabled.value)
  const isDrugInteractionsEnabled = computed(() => drugInteractionsEnabled.value)
  const isBillingSubscriptionEnabled = computed(() => billingSubscriptionEnabled.value)
  const isWhatsNewEnabled = computed(() => whatsNewEnabled.value)
const isComingSoonEnabled = computed(() => comingSoonEnabled.value)
const isVisualPreferencesEnabled = computed(() => visualPreferencesEnabled.value)
const isConceptualPricingEnabled = computed(() => conceptualPricingEnabled.value)
const isAccessibilitySettingsEnabled = computed(() => accessibilitySettingsEnabled.value)
const isDataExportEnabled = computed(() => dataExportEnabled.value)
  const isEmailVerificationEnabled = computed(() => emailVerificationEnabled.value)
  const isTryDemoEnabled = computed(() => tryDemoEnabled.value)
  const isAutoScrollEnabled = computed(() => autoScrollEnabled.value)
  const isLightModeDisabled = computed(() => lightModeDisabled.value)
  const isPricingEnabled = computed(() => pricingEnabled.value)
  const isTermsAndConditionsDisabled = computed(() => termsAndConditionsDisabled.value)
  const isRegistrationDisabled = computed(() => registrationDisabled.value)
  const isGoogleSignupDisabled = computed(() => googleSignupDisabled.value)

  // Actions
  const toggleAIAssistant = () => {
    aiAssistantEnabled.value = !aiAssistantEnabled.value
    saveToLocalStorage()
  }


  const toggleMusic = () => {
    musicEnabled.value = !musicEnabled.value
    saveToLocalStorage()
  }

  const toggleAnimations = () => {
    animationsEnabled.value = !animationsEnabled.value
    saveToLocalStorage()
  }

  const toggleFunMode = () => {
    funModeEnabled.value = !funModeEnabled.value
    // When fun mode is disabled, also disable music
    if (!funModeEnabled.value) {
      musicEnabled.value = false
    }
    saveToLocalStorage()
  }

  const setAIAssistant = (enabled: boolean) => {
    aiAssistantEnabled.value = enabled
    saveToLocalStorage()
  }


  const setMusic = (enabled: boolean) => {
    musicEnabled.value = enabled
    saveToLocalStorage()
  }

  const setSoundsEnabled = (enabled: boolean) => {
    soundsEnabled.value = enabled
    saveToLocalStorage()
  }

  const setAnimations = (enabled: boolean) => {
    animationsEnabled.value = enabled
    saveToLocalStorage()
  }

  const setFunMode = (enabled: boolean) => {
    funModeEnabled.value = enabled
    // When fun mode is disabled, also disable music
    if (!enabled) {
      musicEnabled.value = false
    }
    saveToLocalStorage()
  }

  const setReduceMotion = (enabled: boolean) => {
    reduceMotionEnabled.value = enabled
    // Apply motion reduction to the document
    applyMotionReduction(enabled)
    saveToLocalStorage()
  }

  const setEducationalChallenge = (enabled: boolean) => {
    educationalChallengeEnabled.value = enabled
    saveToLocalStorage()
  }

  const setShowtimeCredits = (enabled: boolean) => {
    showtimeCreditsEnabled.value = enabled
    saveToLocalStorage()
  }

  const setMergeTheory = (enabled: boolean) => {
    mergeTheoryEnabled.value = enabled
    saveToLocalStorage()
  }

  const setFlyingRobot = (enabled: boolean) => {
    flyingRobotEnabled.value = enabled
    saveToLocalStorage()
  }

  const setEducationalResources = (enabled: boolean) => {
    educationalResourcesEnabled.value = enabled
    saveToLocalStorage()
  }

  const setComingSoon = (enabled: boolean) => {
    comingSoonEnabled.value = enabled
    saveToLocalStorage()
  }

  const setVisualPreferences = (enabled: boolean) => {
    visualPreferencesEnabled.value = enabled
    saveToLocalStorage()
  }

const setConceptualPricing = (enabled: boolean) => {
  conceptualPricingEnabled.value = enabled
  saveToLocalStorage()
}

const setAccessibilitySettings = (enabled: boolean) => {
  accessibilitySettingsEnabled.value = enabled
  saveToLocalStorage()
}

const setDataExport = (enabled: boolean) => {
  dataExportEnabled.value = enabled
  saveToLocalStorage()
}

  const setEmailVerification = (enabled: boolean) => {
    emailVerificationEnabled.value = enabled
    saveToLocalStorage()
  }

  const setTryDemo = (enabled: boolean) => {
    tryDemoEnabled.value = enabled
    saveToLocalStorage()
  }

  const setAutoScroll = (enabled: boolean) => {
    autoScrollEnabled.value = enabled
    saveToLocalStorage()
  }

  const setPricing = (enabled: boolean) => {
    pricingEnabled.value = enabled
    saveToLocalStorage()
  }

  // Motion reduction function
  const applyMotionReduction = (enabled: boolean) => {
    if (enabled) {
      document.documentElement.style.setProperty('--animation-duration', '0.01ms')
      document.documentElement.style.setProperty('--animation-iteration-count', '1')
      document.documentElement.classList.add('reduce-motion')
    } else {
      document.documentElement.style.removeProperty('--animation-duration')
      document.documentElement.style.removeProperty('--animation-iteration-count')
      document.documentElement.classList.remove('reduce-motion')
    }
  }

  // Local storage persistence
  const saveToLocalStorage = () => {
    const settings = {
      aiAssistantEnabled: aiAssistantEnabled.value,
      musicEnabled: musicEnabled.value,
      soundsEnabled: soundsEnabled.value,
      animationsEnabled: animationsEnabled.value,
      funModeEnabled: funModeEnabled.value,
      reduceMotionEnabled: reduceMotionEnabled.value,
      educationalChallengeEnabled: educationalChallengeEnabled.value,
      showtimeCreditsEnabled: showtimeCreditsEnabled.value,
      mergeTheoryEnabled: mergeTheoryEnabled.value,
      flyingRobotEnabled: flyingRobotEnabled.value,
      educationalResourcesEnabled: educationalResourcesEnabled.value,
      mobileAppDemoEnabled: mobileAppDemoEnabled.value,
      drugInteractionsEnabled: drugInteractionsEnabled.value,
      billingSubscriptionEnabled: billingSubscriptionEnabled.value,
      whatsNewEnabled: whatsNewEnabled.value,
      comingSoonEnabled: comingSoonEnabled.value,
      visualPreferencesEnabled: visualPreferencesEnabled.value,
      conceptualPricingEnabled: conceptualPricingEnabled.value,
        accessibilitySettingsEnabled: accessibilitySettingsEnabled.value,
        dataExportEnabled: dataExportEnabled.value,
        emailVerificationEnabled: emailVerificationEnabled.value,
        tryDemoEnabled: tryDemoEnabled.value,
        autoScrollEnabled: autoScrollEnabled.value,
        lightModeDisabled: lightModeDisabled.value,
        prioritizeHardcodedValues: prioritizeHardcodedValues.value,
        pricingEnabled: pricingEnabled.value,
        termsAndConditionsDisabled: termsAndConditionsDisabled.value,
        registrationDisabled: registrationDisabled.value,
        googleSignupDisabled: googleSignupDisabled.value
    }
    localStorage.setItem('featureSettings', JSON.stringify(settings))
  }

  const loadFromLocalStorage = () => {
    try {
      const stored = localStorage.getItem('featureSettings')
      if (stored) {
        const settings = JSON.parse(stored)
        aiAssistantEnabled.value = settings.aiAssistantEnabled ?? true
        musicEnabled.value = settings.musicEnabled ?? false
        soundsEnabled.value = settings.soundsEnabled ?? true
        animationsEnabled.value = settings.animationsEnabled ?? true
        funModeEnabled.value = settings.funModeEnabled ?? true
        reduceMotionEnabled.value = settings.reduceMotionEnabled ?? false
        educationalChallengeEnabled.value = settings.educationalChallengeEnabled ?? true
        showtimeCreditsEnabled.value = settings.showtimeCreditsEnabled ?? true
        mergeTheoryEnabled.value = settings.mergeTheoryEnabled ?? true
        flyingRobotEnabled.value = settings.flyingRobotEnabled ?? true
        educationalResourcesEnabled.value = settings.educationalResourcesEnabled ?? true
        mobileAppDemoEnabled.value = settings.mobileAppDemoEnabled ?? true
        drugInteractionsEnabled.value = settings.drugInteractionsEnabled ?? true
        billingSubscriptionEnabled.value = settings.billingSubscriptionEnabled ?? true
        whatsNewEnabled.value = settings.whatsNewEnabled ?? true
        comingSoonEnabled.value = settings.comingSoonEnabled ?? false
        visualPreferencesEnabled.value = settings.visualPreferencesEnabled ?? true
        conceptualPricingEnabled.value = settings.conceptualPricingEnabled ?? true
        accessibilitySettingsEnabled.value = settings.accessibilitySettingsEnabled ?? true
        dataExportEnabled.value = settings.dataExportEnabled ?? true
        emailVerificationEnabled.value = settings.emailVerificationEnabled ?? false
        tryDemoEnabled.value = settings.tryDemoEnabled ?? false
        autoScrollEnabled.value = settings.autoScrollEnabled ?? false
        lightModeDisabled.value = settings.lightModeDisabled ?? false
        prioritizeHardcodedValues.value = settings.prioritizeHardcodedValues ?? false
        pricingEnabled.value = settings.pricingEnabled ?? true
        termsAndConditionsDisabled.value = settings.termsAndConditionsDisabled ?? true
        registrationDisabled.value = settings.registrationDisabled ?? true
        googleSignupDisabled.value = settings.googleSignupDisabled ?? true
        
        // Apply motion reduction on load
        applyMotionReduction(reduceMotionEnabled.value)
      }
    } catch (error) {
      console.error('Failed to load feature settings:', error)
    }
  }

  const resetToDefaults = () => {
    aiAssistantEnabled.value = true
    musicEnabled.value = false
    soundsEnabled.value = true
    animationsEnabled.value = true
    funModeEnabled.value = true
    reduceMotionEnabled.value = false
    educationalChallengeEnabled.value = true
    showtimeCreditsEnabled.value = true
    mergeTheoryEnabled.value = true
    flyingRobotEnabled.value = true
    educationalResourcesEnabled.value = true
    mobileAppDemoEnabled.value = true
    drugInteractionsEnabled.value = true
    billingSubscriptionEnabled.value = true
    whatsNewEnabled.value = true
    comingSoonEnabled.value = false
    visualPreferencesEnabled.value = false
    conceptualPricingEnabled.value = true
    accessibilitySettingsEnabled.value = true
    dataExportEnabled.value = true
    emailVerificationEnabled.value = false
    tryDemoEnabled.value = false
    autoScrollEnabled.value = false
    lightModeDisabled.value = false
    prioritizeHardcodedValues.value = false
    pricingEnabled.value = true
    saveToLocalStorage()
  }

  // Fetch feature settings from backend
  const fetchFeatureSettingsFromBackend = async () => {
    try {
      const response = await apiService.get('/features', undefined, { skipAuth: true })
      if (response) {
        if (typeof response.email_verification_enabled === 'boolean') {
          emailVerificationEnabled.value = response.email_verification_enabled
          console.log('📧 Email verification enabled setting from backend:', response.email_verification_enabled)
        }
        if (typeof response.light_mode_disabled === 'boolean') {
          lightModeDisabled.value = response.light_mode_disabled
          console.log('🌙 Light mode disabled setting from backend:', response.light_mode_disabled)
        }
        if (typeof response.try_demo_enabled === 'boolean') {
          tryDemoEnabled.value = response.try_demo_enabled
          console.log('🎮 Try demo enabled setting from backend:', response.try_demo_enabled)
        }
      }
    } catch (error) {
      console.error('Failed to fetch feature settings from backend:', error)
      // Keep the current values if API call fails
    }
  }

  // Fetch server-side feature flags
  const fetchServerFeatureFlags = async () => {
    try {
      const serverFlags: ApiFeatureFlags = await apiService.get('/features', {}, { skipAuth: true })
      
      // Update prioritize_hardcoded_values flag from server
      if (serverFlags.prioritize_hardcoded_values !== undefined) {
        prioritizeHardcodedValues.value = serverFlags.prioritize_hardcoded_values
      }
      
      // When prioritize_hardcoded_values is true, the server values ARE the hardcoded values
      // (from features.json), so we should apply them to ensure consistency across environments.
      // The "prioritize hardcoded" means the server prioritizes features.json over database,
      // not that we should ignore server values.
      
      // Always apply server values - they represent the authoritative hardcoded values
      
      // Override emailVerificationEnabled with server value
      if (serverFlags.email_verification_enabled !== undefined) {
        emailVerificationEnabled.value = serverFlags.email_verification_enabled
      }

      // Override educationalResourcesEnabled with server value
      if (serverFlags.educational_resources_enabled !== undefined) {
        educationalResourcesEnabled.value = serverFlags.educational_resources_enabled
      }

      // Override mobileAppDemoEnabled with server value
      if (serverFlags.mobile_app_demo_enabled !== undefined) {
        mobileAppDemoEnabled.value = serverFlags.mobile_app_demo_enabled
      }

      // Override drugInteractionsEnabled with server value
      if (serverFlags.drug_interactions_enabled !== undefined) {
        drugInteractionsEnabled.value = serverFlags.drug_interactions_enabled
      }

      // Override billingSubscriptionEnabled with server value
      if (serverFlags.billing_subscription_enabled !== undefined) {
        billingSubscriptionEnabled.value = serverFlags.billing_subscription_enabled
      }

      // Override whatsNewEnabled with server value
      if (serverFlags.whats_new_enabled !== undefined) {
        whatsNewEnabled.value = serverFlags.whats_new_enabled
      }

      // Override aiAssistantEnabled with server value
      if (serverFlags.ai_assistant_enabled !== undefined) {
        aiAssistantEnabled.value = serverFlags.ai_assistant_enabled
      }

      // Override visualPreferencesEnabled with server value
      if (serverFlags.visual_preferences_enabled !== undefined) {
        visualPreferencesEnabled.value = serverFlags.visual_preferences_enabled
      }

      // Override lightModeDisabled with server value
      if (serverFlags.light_mode_disabled !== undefined) {
        lightModeDisabled.value = serverFlags.light_mode_disabled
      }

      // Override tryDemoEnabled with server value
      if (serverFlags.try_demo_enabled !== undefined) {
        tryDemoEnabled.value = serverFlags.try_demo_enabled
      }

      // Override pricingEnabled with server value
      if (serverFlags.pricing_enabled !== undefined) {
        pricingEnabled.value = serverFlags.pricing_enabled
      }

      // Override termsAndConditionsDisabled with server value
      if (serverFlags.terms_and_conditions_disabled !== undefined) {
        termsAndConditionsDisabled.value = serverFlags.terms_and_conditions_disabled
      }

      // Override registrationDisabled with server value
      if (serverFlags.registration_disabled !== undefined) {
        registrationDisabled.value = serverFlags.registration_disabled
      }

      // Override googleSignupDisabled with server value
      if (serverFlags.google_signup_disabled !== undefined) {
        googleSignupDisabled.value = serverFlags.google_signup_disabled
      }

      // Save server values to localStorage after applying them (only if not prioritizing hardcoded)
      // This ensures server values persist and take precedence
      saveToLocalStorage()
      
      // Store timestamp of last server fetch for cache invalidation
      localStorage.setItem('featureFlagsLastFetch', Date.now().toString())
    } catch (error) {
      console.error('Failed to fetch server feature flags:', error)
      // Continue with local settings if server fetch fails
    }
  }

  // Initialize: Load from localStorage first (for immediate UI), then fetch from server (to override)
  loadFromLocalStorage()
  
  // Fetch server flags asynchronously - server values will override localStorage
  fetchServerFeatureFlags()

  // Set up periodic refresh every 5 minutes to ensure users get updates
  let refreshInterval: ReturnType<typeof setInterval> | null = null
  
  const startPeriodicRefresh = () => {
    // Clear existing interval if any
    if (refreshInterval) {
      clearInterval(refreshInterval)
    }
    // Refresh every 5 minutes (300000 ms)
    refreshInterval = setInterval(() => {
      fetchServerFeatureFlags()
    }, 5 * 60 * 1000)
  }

  const stopPeriodicRefresh = () => {
    if (refreshInterval) {
      clearInterval(refreshInterval)
      refreshInterval = null
    }
  }

  // Start periodic refresh
  startPeriodicRefresh()

  // Refresh flags when page becomes visible (user switches back to tab)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        // Page became visible, refresh flags immediately
        fetchServerFeatureFlags()
      }
    })
  }

  return {
    // State
    aiAssistantEnabled,
    musicEnabled,
    soundsEnabled,
    animationsEnabled,
    funModeEnabled,
    reduceMotionEnabled,
    educationalChallengeEnabled,
    showtimeCreditsEnabled,
    mergeTheoryEnabled,
    flyingRobotEnabled,
    educationalResourcesEnabled,
    mobileAppDemoEnabled,
    drugInteractionsEnabled,
    billingSubscriptionEnabled,
    whatsNewEnabled,
    comingSoonEnabled,
    visualPreferencesEnabled,
    conceptualPricingEnabled,
    accessibilitySettingsEnabled,
    dataExportEnabled,
    emailVerificationEnabled,
    tryDemoEnabled,
    autoScrollEnabled,
    lightModeDisabled,
    prioritizeHardcodedValues,
    pricingEnabled,
    termsAndConditionsDisabled,
    registrationDisabled,
    googleSignupDisabled,
    
    // Computed
    isAIAssistantEnabled,
    isMusicEnabled,
    areSoundsEnabled,
    areAnimationsEnabled,
    isFunModeEnabled,
    isReduceMotionEnabled,
    isEducationalChallengeEnabled,
    isShowtimeCreditsEnabled,
    isMergeTheoryEnabled,
    isFlyingRobotEnabled,
    isEducationalResourcesEnabled,
    isMobileAppDemoEnabled,
    isDrugInteractionsEnabled,
    isBillingSubscriptionEnabled,
    isWhatsNewEnabled,
    isComingSoonEnabled,
    isVisualPreferencesEnabled,
    isConceptualPricingEnabled,
    isAccessibilitySettingsEnabled,
    isDataExportEnabled,
    isEmailVerificationEnabled,
    isTryDemoEnabled,
    isAutoScrollEnabled,
    isLightModeDisabled,
    isPricingEnabled,
    isTermsAndConditionsDisabled,
    isRegistrationDisabled,
    isGoogleSignupDisabled,
    
    // Actions
    toggleAIAssistant,
    toggleMusic,
    toggleAnimations,
    toggleFunMode,
    setAIAssistant,
    setMusic,
    setSoundsEnabled,
    setAnimations,
    setFunMode,
    setReduceMotion,
    setEducationalChallenge,
    setShowtimeCredits,
    setMergeTheory,
    setFlyingRobot,
    setEducationalResources,
    setComingSoon,
    setVisualPreferences,
    setConceptualPricing,
    setAccessibilitySettings,
    setDataExport,
    setEmailVerification,
    setTryDemo,
    setAutoScroll,
    setPricing,
    resetToDefaults,
    fetchFeatureSettingsFromBackend,
    fetchServerFeatureFlags,
    startPeriodicRefresh,
    stopPeriodicRefresh
  }
})
